// Purpose: Contains the narrow, scoped API-gap bridges that Lara cannot express.
export const utilityStyles = ({ dt }) => `
/* Inertia injects its progress styles outside PrimeVue. Keep the BayHive
   indicator in sync with theme changes without changing the shared entry. */
html[data-ui-brand='bayhive'] #nprogress .bar {
  background: var(--p-primary-border-color, ${dt('primary.color')});
}

html[data-ui-brand='bayhive'] #nprogress .peg {
  box-shadow: 0 0 10px var(--p-primary-border-color, ${dt('primary.color')}), 0 0 5px var(--p-primary-border-color, ${dt('primary.color')});
}

html[data-ui-brand='bayhive'] #nprogress .spinner-icon {
  border-top-color: var(--p-primary-border-color, ${dt('primary.color')});
  border-left-color: var(--p-primary-border-color, ${dt('primary.color')});
}

/* PrimeVue styles TabPanel's :focus-visible state through tabs.tabpanel tokens,
   but leaves ordinary pointer focus to the browser's native outline. Suppress
   that click-only outline; the preset keeps keyboard focus visibly purple. */
html[data-ui-brand='bayhive'] .p-tabpanel:focus:not(:focus-visible) {
  outline: none;
}

/* Tooltip themes style the popup, not its Font Awesome trigger. Keep focusable
   info glyphs free of native blue click outlines and visibly purple on keyboard
   focus. The tabindex requirement excludes decorative and Button icon spans. */
html[data-ui-brand='bayhive'] :is(.fa-circle-info, .fa-info-circle)[tabindex]:focus:not(:focus-visible) {
  outline: none;
}

html[data-ui-brand='bayhive'] :is(.fa-circle-info, .fa-info-circle)[tabindex]:focus-visible {
  border-radius: ${dt('content.border.radius')};
  outline: 2px solid ${dt('focus.ring.color')};
  outline-offset: 2px;
}

/* Lara Cards intentionally expose elevation rather than a border. BayHive
   sections often sit on the same surface as their modal or page container,
   so retain one token-based edge that remains visible in both schemes. */
html[data-ui-brand='bayhive'] .p-card {
  border: 1px solid light-dark(${dt('surface.300')}, ${dt('surface.600')});
}

/* Lara's outlined fields only change their border on hover. BayHive keeps the
   component geometry but restores its full light-purple field surface: pointer
   hover remains quiet, while keyboard and active focus retain a purple edge. */
html[data-ui-brand='bayhive'] :is(
  .p-inputtext,
  .p-textarea,
  .p-inputtags,
  .p-autocomplete-input-multiple,
  .p-select,
  .p-multiselect,
  .p-treeselect,
  .p-cascadeselect
):not(:disabled, .p-disabled, .p-invalid):hover {
  background: ${dt('form.field.filled.hover.background')};
  border-color: ${dt('form.field.border.color')};
}

html[data-ui-brand='bayhive'] :is(
  .p-inputtext,
  .p-textarea,
  .p-inputtags,
  .p-autocomplete-input-multiple,
  .p-select,
  .p-multiselect,
  .p-treeselect,
  .p-cascadeselect
):not(:disabled, .p-disabled, .p-invalid):is(
  :focus,
  :focus-within,
  .p-focus,
  .p-inputwrapper-focus
) {
  background: ${dt('form.field.filled.focus.background')};
  border-color: ${dt('form.field.focus.border.color')};
}

/* AutoComplete and DatePicker use adjacent input and trigger elements. Give
   both halves the same visual state without changing their portal behavior. */
html[data-ui-brand='bayhive'] :is(
  .p-autocomplete:has(> .p-autocomplete-dropdown),
  .p-datepicker:has(> .p-datepicker-dropdown)
):not(.p-invalid):not(:has(> :disabled)):hover > :is(
  .p-autocomplete-input,
  .p-autocomplete-input-multiple,
  .p-autocomplete-dropdown,
  .p-datepicker-input,
  .p-datepicker-dropdown
) {
  background: ${dt('form.field.filled.hover.background')};
  border-color: ${dt('form.field.border.color')};
}

html[data-ui-brand='bayhive'] :is(
  .p-autocomplete:has(> .p-autocomplete-dropdown),
  .p-datepicker:has(> .p-datepicker-dropdown)
):not(.p-invalid):is(.p-focus, .p-inputwrapper-focus, :focus-within) > :is(
  .p-autocomplete-input,
  .p-autocomplete-input-multiple,
  .p-autocomplete-dropdown,
  .p-datepicker-input,
  .p-datepicker-dropdown
) {
  background: ${dt('form.field.filled.focus.background')};
  border-color: ${dt('form.field.focus.border.color')};
}

/* PrimeVue shares disabled field surfaces and text through semantic tokens,
   but it has no matching cross-component disabled-border token. Keep only the
   unavailable state neutral and distinct; editable fields retain their normal
   BayHive border and focus behavior. */
html[data-ui-brand='bayhive'] :is(
  .p-inputtext:disabled,
  .p-textarea:disabled,
  .p-inputtags.p-disabled,
  .p-select.p-disabled,
  .p-multiselect.p-disabled,
  .p-treeselect.p-disabled,
  .p-cascadeselect.p-disabled,
  .p-listbox.p-disabled
) {
  border-color: ${dt('form.field.disabled.border.color')};
}

html[data-ui-brand='bayhive'] :is(
  .p-select.p-disabled,
  .p-multiselect.p-disabled,
  .p-treeselect.p-disabled,
  .p-cascadeselect.p-disabled,
  .p-inputtags.p-disabled,
  .p-listbox.p-disabled
) {
  background: ${dt('form.field.disabled.background')};
  color: ${dt('form.field.disabled.color')};
}

html[data-ui-brand='bayhive'] :is(
  .p-select.p-disabled .p-select-dropdown,
  .p-multiselect.p-disabled .p-multiselect-dropdown,
  .p-treeselect.p-disabled .p-treeselect-dropdown,
  .p-cascadeselect.p-disabled .p-cascadeselect-dropdown
) {
  background: ${dt('form.field.disabled.background')};
  border-color: ${dt('form.field.disabled.border.color')};
  color: ${dt('form.field.disabled.color')};
}

/* AutoComplete and DatePicker expose a second button alongside their input.
   Give that button the same unavailable surface as the disabled text field. */
html[data-ui-brand='bayhive'] :is(
  .p-autocomplete,
  .p-datepicker
):has(> :disabled) > :is(
  .p-autocomplete-input,
  .p-autocomplete-input-multiple,
  .p-autocomplete-dropdown,
  .p-datepicker-input,
  .p-datepicker-dropdown
) {
  background: ${dt('form.field.disabled.background')};
  border-color: ${dt('form.field.disabled.border.color')};
  color: ${dt('form.field.disabled.color')};
}

html[data-ui-brand='bayhive'] .p-inputnumber:has(.p-inputnumber-input:disabled) :is(
  .p-inputnumber-input,
  .p-inputnumber-button
) {
  background: ${dt('form.field.disabled.background')};
  border-color: ${dt('form.field.disabled.border.color')};
  color: ${dt('form.field.disabled.color')};
}
html[data-ui-brand='bayhive'] .p-autocomplete:has(> .p-autocomplete-dropdown).p-focus {
  border-radius: ${dt('form.field.border.radius')};
  box-shadow: ${dt('form.field.focus.ring.shadow')};
  outline: ${dt('form.field.focus.ring.width')} ${dt('form.field.focus.ring.style')} ${dt('form.field.focus.ring.color')};
  outline-offset: ${dt('form.field.focus.ring.offset')};
}

html[data-ui-brand='bayhive'] .p-autocomplete:has(> .p-autocomplete-dropdown).p-focus :is(
  .p-autocomplete-input,
  .p-autocomplete-input-multiple
) {
  box-shadow: none;
  outline: 0;
}

/* PrimeUI Scheduler is not covered by the PrimeVue preset. Its visual tokens
   inherit BayHive semantic colors while the runtime layout stays scheduler-owned. */
html[data-ui-brand='bayhive'] .p-scheduler {
  --p-scheduler-background: ${dt('content.background')};
  --p-scheduler-border-color: ${dt('content.border.color')};
  --p-scheduler-scroll-rail-border-color: ${dt('content.border.color')};
  --p-scheduler-color: ${dt('text.color')};
  --p-scheduler-header-background: ${dt('content.background')};
  --p-scheduler-title-color: ${dt('text.color')};
  --p-scheduler-weekday-background: ${dt('content.hover.background')};
  --p-scheduler-weekday-color: ${dt('text.color')};
  --p-scheduler-time-label-color: ${dt('text.color')};
  --p-scheduler-timezone-label-color: ${dt('text.color')};
  --p-scheduler-resource-label-color: ${dt('text.color')};
  --p-scheduler-week-number-color: ${dt('text.color')};
  --p-scheduler-event-background: ${dt('content.hover.background')};
  --p-scheduler-event-hover-background: color-mix(
    in srgb,
    ${dt('content.hover.background')} 88%,
    ${dt('text.color')}
  );
  --p-scheduler-event-color: ${dt('text.color')};
  --p-scheduler-event-title-color: ${dt('text.color')};
  --p-scheduler-event-time-color: ${dt('text.color')};
  --p-scheduler-event-border-color: ${dt('text.color')};
  --p-bayhive-scheduler-event-edge-color: ${dt('text.color')};
  --p-bayhive-scheduler-event-current-edge-color: var(--p-primary-border-color, ${dt('primary.color')});
  --p-scheduler-event-resize-handle-color: var(--p-primary-border-color, ${dt('primary.color')});
  --p-scheduler-event-resize-handle-active-color: var(--p-primary-border-color, ${dt('primary.color')});
  --p-scheduler-now-indicator-color: ${dt('danger.color')};
  --p-scheduler-range-background: ${dt('highlight.background')};
  --p-scheduler-range-edge-background: ${dt('highlight.focus.background')};
  --p-scheduler-range-color: ${dt('highlight.color')};
  --p-scheduler-non-business-background: color-mix(
    in srgb,
    ${dt('text.muted.color')} 10%,
    ${dt('content.background')}
  );
  --p-scheduler-non-business-hours-background: color-mix(
    in srgb,
    ${dt('text.muted.color')} 16%,
    ${dt('content.background')}
  );
  --p-scheduler-business-border-color: ${dt('content.border.color')};
  --p-scheduler-blocked-interval-background: color-mix(
    in srgb,
    ${dt('text.muted.color')} 24%,
    ${dt('content.background')}
  );
  --p-scheduler-blocked-interval-stripe-color: color-mix(
    in srgb,
    ${dt('text.muted.color')} 38%,
    ${dt('content.background')}
  );
  --p-scheduler-blocked-interval-text-color: ${dt('text.muted.color')};
  --p-scheduler-blocked-interval-border-color: ${dt('content.border.color')};
}

/* PrimeUI hard-codes blocked intervals above normal events. Its public API has
   no layer hook, so lower only that non-interactive background interval. */
@keyframes bayhive-scheduler-blocked-interval-layer {
  to {
    z-index: 0;
  }
}

html[data-ui-brand='bayhive'] .p-scheduler .p-scheduler-blocked-interval {
  animation: bayhive-scheduler-blocked-interval-layer 1ms step-end both;
}

html[data-ui-brand='bayhive'] .p-scheduler .p-scheduler-blocked-interval-label {
  pointer-events: none;
}

/* Font Awesome glyph metrics differ from PrimeVue's inline SVG metrics. */
html[data-ui-brand='bayhive'] :is(
  .p-inputicon,
  .p-password-toggle-mask-icon,
  .p-password-clear-icon,
  .p-autocomplete-clear-icon,
  .p-datepicker-input-icon-container,
  .p-datepicker-clear-icon,
  .p-inputnumber-clear-icon
):is(
  [class^='fa-'],
  [class*=' fa-'],
  :has(> [class^='fa-']),
  :has(> [class*=' fa-'])
) {
  align-items: center;
  display: inline-flex;
  justify-content: center;
  line-height: 1;
  margin-block-start: 0;
  top: 50%;
  transform: translateY(-50%);
}

/* Keep the app's scroll-edge dialog wiring functional without replacing Lara's
   dialog spacing, type, surfaces, or shadows. */
html[data-ui-brand='bayhive'] .p-dialog {
  overflow: hidden;
}

/* Use each dialog's own measured content or explicit layout size, never a
   generic minimum height. Custom media frames and PrimeVue's maximize action
   retain ownership of their dimensions. */
html[data-ui-brand='bayhive'] .p-dialog.bayhive-dialog:where(:not(.p-dialog-maximized, .bayhive-dialog--custom)) {
  max-width: calc(100vw - var(--p-bayhive-layout-dialog-viewport-inset, 2rem));
  max-height: calc(100dvh - var(--p-bayhive-layout-dialog-viewport-inset, 2rem));
}

html[data-ui-brand='bayhive'] .p-dialog.bayhive-dialog[data-bayhive-dialog-sized]:where(:not(.p-dialog-maximized, .bayhive-dialog--custom)) {
  width: min(var(--bayhive-dialog-width), calc(100vw - var(--p-bayhive-layout-dialog-viewport-inset, 2rem)));
  height: min(var(--bayhive-dialog-height), calc(100dvh - var(--p-bayhive-layout-dialog-viewport-inset, 2rem)));
}

html[data-ui-brand='bayhive'] .bayhive-dialog > .p-dialog-content {
  flex: 1 1 auto;
  min-height: 0;
  scrollbar-gutter: stable;
}

html[data-ui-brand='bayhive'] :is(.p-dialog-header, .p-dialog-footer) {
  position: relative;
  z-index: 1;
}

html[data-ui-brand='bayhive'] .p-dialog[data-scroll-above] > .p-dialog-header {
  box-shadow: 0 1px 0 var(--p-content-border-color);
}

html[data-ui-brand='bayhive'] .p-dialog[data-scroll-below] > .p-dialog-footer {
  box-shadow: 0 -1px 0 var(--p-content-border-color);
}

/* Lara exposes a DataTable row focus token, but ordinary row selection only
   changes the fill. Carry the same token-driven purple outline through both
   keyboard focus and a selected selectable row so the active record remains
   identifiable without redesigning table density or surfaces. */
html[data-ui-brand='bayhive'] .p-datatable .p-datatable-tbody > tr[data-p-selectable-row='true']:is(
  :focus-visible,
  [data-p-selected='true']
) {
  outline: ${dt('datatable.row.focus.ring.width')} ${dt('datatable.row.focus.ring.style')} ${dt('datatable.row.focus.ring.color')};
  outline-offset: ${dt('datatable.row.focus.ring.offset')};
}

/* API gap: Select and AutoComplete expose option fill colors, but no selected
   edge or option-focus ring. Keep the dark selected cue distinct from keyboard
   focus without changing option geometry or the wrappers' focus behavior. */
html[data-ui-brand='bayhive'][data-bs-theme='dark'] :is(.p-select-option-selected, .p-autocomplete-option-selected) {
  box-shadow: inset 3px 0 0 ${dt('focus.ring.color')};
}

html[data-ui-brand='bayhive'][data-bs-theme='dark'] .p-select-option[data-p-focused='true']:not([data-p-disabled='true']),
html[data-ui-brand='bayhive'][data-bs-theme='dark'] .p-autocomplete-overlay:not(.bayhive-autocomplete-overlay--header-active) .p-autocomplete-option[data-p-focused='true']:not([data-p-disabled='true']) {
  outline: 2px solid ${dt('focus.ring.color')};
  outline-offset: -2px;
}

/* API gap: Button tokens do not expose a distinct focus-visible visual state
   for every severity and variant. Keep keyboard focus aligned with Lara's
   current hover and form-field tokens without changing button geometry. */
html[data-ui-brand='bayhive'] .p-button {
  --p-bayhive-button-focus-background: var(--p-button-primary-hover-background);
  --p-bayhive-button-focus-border-color: var(--p-button-primary-hover-border-color);
  --p-bayhive-button-focus-color: var(--p-button-primary-hover-color);
}

html[data-ui-brand='bayhive'] .p-button-secondary {
  --p-bayhive-button-focus-background: var(--p-button-secondary-hover-background);
  --p-bayhive-button-focus-border-color: var(--p-button-secondary-hover-border-color);
  --p-bayhive-button-focus-color: var(--p-button-secondary-hover-color);
}

html[data-ui-brand='bayhive'] .p-button-success {
  --p-bayhive-button-focus-background: var(--p-button-success-hover-background);
  --p-bayhive-button-focus-border-color: var(--p-button-success-hover-border-color);
  --p-bayhive-button-focus-color: var(--p-button-success-hover-color);
}

html[data-ui-brand='bayhive'] .p-button-info {
  --p-bayhive-button-focus-background: var(--p-button-info-hover-background);
  --p-bayhive-button-focus-border-color: var(--p-button-info-hover-border-color);
  --p-bayhive-button-focus-color: var(--p-button-info-hover-color);
}

html[data-ui-brand='bayhive'] .p-button-warn,
html[data-ui-brand='bayhive'] .p-button-warning {
  --p-bayhive-button-focus-background: var(--p-button-warn-hover-background);
  --p-bayhive-button-focus-border-color: var(--p-button-warn-hover-border-color);
  --p-bayhive-button-focus-color: var(--p-button-warn-hover-color);
}

html[data-ui-brand='bayhive'] .p-button-danger,
html[data-ui-brand='bayhive'] .p-button-error {
  --p-bayhive-button-focus-background: var(--p-button-danger-hover-background);
  --p-bayhive-button-focus-border-color: var(--p-button-danger-hover-border-color);
  --p-bayhive-button-focus-color: var(--p-button-danger-hover-color);
}

html[data-ui-brand='bayhive'] .p-button-contrast {
  --p-bayhive-button-focus-background: var(--p-button-contrast-hover-background);
  --p-bayhive-button-focus-border-color: var(--p-button-contrast-hover-border-color);
  --p-bayhive-button-focus-color: var(--p-button-contrast-hover-color);
}

html[data-ui-brand='bayhive'] .p-button-outlined {
  --p-bayhive-button-focus-background: var(--p-button-outlined-primary-hover-background);
  --p-bayhive-button-focus-border-color: var(--p-button-outlined-primary-border-color);
  --p-bayhive-button-focus-color: var(--p-button-outlined-primary-color);
}

html[data-ui-brand='bayhive'] .p-button-outlined.p-button-secondary {
  --p-bayhive-button-focus-background: var(--p-button-outlined-secondary-hover-background);
  --p-bayhive-button-focus-border-color: var(--p-form-field-hover-border-color);
  --p-bayhive-button-focus-color: var(--p-button-outlined-secondary-hover-color);
}

html[data-ui-brand='bayhive'] .p-button-outlined.p-button-success {
  --p-bayhive-button-focus-background: var(--p-button-outlined-success-hover-background);
  --p-bayhive-button-focus-border-color: var(--p-button-outlined-success-border-color);
  --p-bayhive-button-focus-color: var(--p-button-outlined-success-color);
}

html[data-ui-brand='bayhive'] .p-button-outlined.p-button-info {
  --p-bayhive-button-focus-background: var(--p-button-outlined-info-hover-background);
  --p-bayhive-button-focus-border-color: var(--p-button-outlined-info-border-color);
  --p-bayhive-button-focus-color: var(--p-button-outlined-info-color);
}

html[data-ui-brand='bayhive'] .p-button-outlined:is(.p-button-warn, .p-button-warning) {
  --p-bayhive-button-focus-background: var(--p-button-outlined-warn-hover-background);
  --p-bayhive-button-focus-border-color: var(--p-button-outlined-warn-border-color);
  --p-bayhive-button-focus-color: var(--p-button-outlined-warn-color);
}

html[data-ui-brand='bayhive'] .p-button-outlined:is(.p-button-danger, .p-button-error) {
  --p-bayhive-button-focus-background: var(--p-button-outlined-danger-hover-background);
  --p-bayhive-button-focus-border-color: var(--p-button-outlined-danger-border-color);
  --p-bayhive-button-focus-color: var(--p-button-outlined-danger-color);
}

html[data-ui-brand='bayhive'] .p-button-outlined.p-button-contrast {
  --p-bayhive-button-focus-background: var(--p-button-outlined-contrast-hover-background);
  --p-bayhive-button-focus-border-color: var(--p-button-outlined-contrast-border-color);
  --p-bayhive-button-focus-color: var(--p-button-outlined-contrast-color);
}

html[data-ui-brand='bayhive'] .p-button-text {
  --p-bayhive-button-focus-background: var(--p-button-text-primary-hover-background);
  --p-bayhive-button-focus-border-color: transparent;
  --p-bayhive-button-focus-color: var(--p-button-text-primary-color);
}

html[data-ui-brand='bayhive'] .p-button-text.p-button-secondary {
  --p-bayhive-button-focus-background: var(--p-button-text-secondary-hover-background);
  --p-bayhive-button-focus-color: var(--p-button-text-secondary-color);
}

html[data-ui-brand='bayhive'] .p-button-text.p-button-success {
  --p-bayhive-button-focus-background: var(--p-button-text-success-hover-background);
  --p-bayhive-button-focus-color: var(--p-button-text-success-color);
}

html[data-ui-brand='bayhive'] .p-button-text.p-button-info {
  --p-bayhive-button-focus-background: var(--p-button-text-info-hover-background);
  --p-bayhive-button-focus-color: var(--p-button-text-info-color);
}

html[data-ui-brand='bayhive'] .p-button-text:is(.p-button-warn, .p-button-warning) {
  --p-bayhive-button-focus-background: var(--p-button-text-warn-hover-background);
  --p-bayhive-button-focus-color: var(--p-button-text-warn-color);
}

html[data-ui-brand='bayhive'] .p-button-text:is(.p-button-danger, .p-button-error) {
  --p-bayhive-button-focus-background: var(--p-button-text-danger-hover-background);
  --p-bayhive-button-focus-color: var(--p-button-text-danger-color);
}

html[data-ui-brand='bayhive'] .p-button-text.p-button-contrast {
  --p-bayhive-button-focus-background: var(--p-button-text-contrast-hover-background);
  --p-bayhive-button-focus-color: var(--p-button-text-contrast-color);
}

/* Link actions keep their transparent treatment in either color mode. Their
   focus indicator belongs to the surrounding surface, not a filled button. */
html[data-ui-brand='bayhive'] .p-button-link {
  --p-bayhive-button-focus-background: transparent;
  --p-bayhive-button-focus-border-color: transparent;
  --p-bayhive-button-focus-color: var(--p-button-link-color);
}

html[data-ui-brand='bayhive']:not([data-bs-theme='dark']) .p-button:not(:disabled):focus-visible,
html[data-ui-brand='bayhive'][data-bs-theme='dark'] .p-button:not(:disabled):focus-visible:not(:active) {
  background: var(--p-bayhive-button-focus-background);
  border-color: var(--p-bayhive-button-focus-border-color);
  color: var(--p-bayhive-button-focus-color);
}

html[data-ui-brand='bayhive'] .p-button-link:focus:not(:focus-visible) {
  outline: none;
}

html[data-ui-brand='bayhive'] .p-button-link:not(:disabled):focus-visible {
  outline: 2px solid ${dt('focus.ring.color')};
  outline-offset: 2px;
  box-shadow: none;
}

/* Button has no disabled-fill tokens; reuse the shared unavailable field
   colors for filled secondary actions instead of fading their enabled fill. */
html[data-ui-brand='bayhive'] .p-button-secondary:not(.p-button-outlined, .p-button-text, .p-button-link):is(:disabled, .p-disabled) {
  background: ${dt('form.field.disabled.background')};
  border-color: ${dt('form.field.disabled.border.color')};
  color: ${dt('form.field.disabled.color')};
  opacity: 1;
}

html[data-ui-brand='bayhive'] .p-button-outlined.p-button-secondary:not(:disabled):is(:hover, :focus-visible) {
  border-color: ${dt('form.field.hover.border.color')};
}

html[data-ui-brand='bayhive'] .p-button-outlined.p-button-secondary:not(:disabled):active {
  border-color: var(--p-primary-border-color, ${dt('primary.active.color')});
}

/* The owned multiple-selection wrapper measures chip rows and renders its
   overflow summary. Its compact chip geometry is part of that behavior, so
   keep it here rather than leaking it to every AutoComplete. */
html[data-ui-brand='bayhive'] .bayhive-autocomplete-shell {
  /* Lara owns the type scale. This wrapper deliberately owns the selected
     chip/control presentation because it is not PrimeVue's stock multiple
     AutoComplete experience. */
  --p-bayhive-autocomplete-chip-font-size: ${dt('form.field.font.size')};
  --p-bayhive-autocomplete-chip-height: 1.75rem;
  --p-bayhive-autocomplete-chip-icon-size: 0.8125rem;
  --p-bayhive-autocomplete-chip-padding-x: 0.625rem;
  --p-bayhive-autocomplete-control-height: max(2.375rem, var(--p-bayhive-touch-min-size, 0px));
  --p-bayhive-autocomplete-input-padding-y: 0.25rem;
  display: inline-block;
  max-width: 100%;
  min-width: 0;
  position: relative;
}

/* PrimeVue has no wrapper-scoped token surface for the selected chips. */
html[data-ui-brand='bayhive'] .bayhive-autocomplete-shell .p-autocomplete-chip.p-chip {
  align-items: center;
  background: var(--p-bayhive-autocomplete-chip-background, light-dark(${dt('bayhive.purple.700')}, ${dt('primary.300')}));
  border-radius: ${dt('border.radius.sm')};
  box-sizing: border-box;
  color: var(--p-bayhive-autocomplete-chip-color, light-dark(${dt('surface.0')}, ${dt('primary.contrast.color')}));
  gap: ${dt('bayhive.spacing.xs')};
  height: var(--p-bayhive-autocomplete-chip-height);
  justify-content: center;
  min-height: var(--p-bayhive-autocomplete-chip-height);
  padding: 0.125rem var(--p-bayhive-autocomplete-chip-padding-x);
}

html[data-ui-brand='bayhive'] .bayhive-autocomplete-shell .p-autocomplete-chip-item.p-focus .p-autocomplete-chip.p-chip {
  background: var(--p-bayhive-autocomplete-chip-focus-background, light-dark(${dt('bayhive.purple.800')}, ${dt('primary.200')}));
  color: var(--p-bayhive-autocomplete-chip-focus-color, light-dark(${dt('surface.0')}, ${dt('primary.contrast.color')}));
}

/* AutoComplete tracks chip keyboard focus separately from DOM focus, without
   a chip focus-ring token. Custom brands supply a ring checked against the
   chip fill; the stock dark theme retains its existing inset outline. */
html[data-ui-brand='bayhive'] .bayhive-autocomplete-shell .p-autocomplete-chip[data-p-focused='true'] {
  outline: 2px solid var(--p-bayhive-autocomplete-chip-focus-ring-color, transparent);
  outline-offset: -2px;
}

html[data-ui-brand='bayhive'][data-bs-theme='dark'] .bayhive-autocomplete-shell .p-autocomplete-chip[data-p-focused='true'] {
  outline-color: var(--p-bayhive-autocomplete-chip-focus-ring-color, ${dt('surface.950')});
}

html[data-ui-brand='bayhive'] .bayhive-autocomplete-shell:has(> .p-autocomplete:is(.bayhive-autocomplete--small, :has([data-p~='small']))) {
  --p-bayhive-autocomplete-chip-font-size: ${dt('form.field.sm.font.size')};
  --p-bayhive-autocomplete-chip-height: 1.5rem;
  --p-bayhive-autocomplete-chip-icon-size: 0.75rem;
  --p-bayhive-autocomplete-chip-padding-x: 0.5rem;
  --p-bayhive-autocomplete-control-height: max(2rem, var(--p-bayhive-touch-min-size, 0px));
  --p-bayhive-autocomplete-input-padding-y: 0.1875rem;
}

html[data-ui-brand='bayhive'] .bayhive-autocomplete-shell:has(> .p-autocomplete:is(.bayhive-autocomplete--large, :has([data-p~='large']))) {
  --p-bayhive-autocomplete-chip-font-size: ${dt('form.field.lg.font.size')};
  --p-bayhive-autocomplete-chip-height: 2rem;
  --p-bayhive-autocomplete-chip-icon-size: 0.875rem;
  --p-bayhive-autocomplete-chip-padding-x: 0.75rem;
  --p-bayhive-autocomplete-control-height: max(2.875rem, var(--p-bayhive-touch-min-size, 0px));
  --p-bayhive-autocomplete-input-padding-y: 0.375rem;
}

html[data-ui-brand='bayhive'] :is(
  .bayhive-autocomplete-shell--fluid,
  .bayhive-autocomplete-shell--fluid > .p-autocomplete
) {
  width: 100%;
}

/* Stable chip rows let the wrapper keep two rows visible and summarize the
   remainder as +N more. */
html[data-ui-brand='bayhive'] .bayhive-autocomplete-shell .p-autocomplete.bayhive-autocomplete--selection-overflow .p-autocomplete-input-multiple {
  align-content: center;
  gap: ${dt('bayhive.spacing.xs')};
  padding-block: var(--p-bayhive-autocomplete-input-padding-y);
}

html[data-ui-brand='bayhive'] .bayhive-autocomplete-shell .p-autocomplete.bayhive-autocomplete--selection-overflow .p-autocomplete-chip-item {
  align-items: center;
  display: flex;
  height: var(--p-bayhive-autocomplete-chip-height);
}

html[data-ui-brand='bayhive'] .bayhive-autocomplete-shell .p-autocomplete-chip .p-chip-label {
  font-size: var(--p-bayhive-autocomplete-chip-font-size);
  line-height: 1;
}

html[data-ui-brand='bayhive'] .bayhive-autocomplete-shell .p-autocomplete-chip .p-chip-remove-icon {
  align-self: center;
  color: currentColor;
  font-size: var(--p-bayhive-autocomplete-chip-icon-size);
  height: var(--p-bayhive-autocomplete-chip-icon-size);
  width: var(--p-bayhive-autocomplete-chip-icon-size);
}

/* PrimeVue fixes this multiple-mode input at 1rem, even when the wrapper is
   small. Correct only the small placeholder/typed text; the custom wrapper
   continues to own the control and chip presentation. */
html[data-ui-brand='bayhive'] .bayhive-autocomplete-shell:has(> .p-autocomplete:is(.bayhive-autocomplete--small, :has([data-p~='small']))) .p-autocomplete-input-chip input {
  font-size: ${dt('form.field.sm.font.size')};
}

html[data-ui-brand='bayhive'] .bayhive-autocomplete-shell .p-autocomplete.bayhive-autocomplete--selection-overflow {
  align-items: stretch;
  height: auto;
}

html[data-ui-brand='bayhive'] .bayhive-autocomplete-shell .p-autocomplete.bayhive-autocomplete--selection-overflow :is(
  .p-autocomplete-input-multiple,
  .p-autocomplete-dropdown
) {
  height: auto;
}

html[data-ui-brand='bayhive'] .bayhive-autocomplete-shell .p-autocomplete.bayhive-autocomplete--selection-overflow .p-autocomplete-dropdown {
  inline-size: var(--p-bayhive-autocomplete-control-height);
  min-inline-size: var(--p-bayhive-autocomplete-control-height);
}

html[data-ui-brand='bayhive'] .bayhive-autocomplete-shell .p-autocomplete.bayhive-autocomplete--selection-overflow .p-autocomplete-input-multiple {
  max-height: none;
  overflow-y: hidden;
}

html[data-ui-brand='bayhive'] .bayhive-autocomplete-shell .p-autocomplete.bayhive-autocomplete--selection-overflow:not(:has(.p-autocomplete-chip-item)),
html[data-ui-brand='bayhive'] .bayhive-autocomplete-shell .p-autocomplete.bayhive-autocomplete--selection-overflow:not(:has(.p-autocomplete-chip-item)) :is(
  .p-autocomplete-input-multiple,
  .p-autocomplete-dropdown
) {
  height: var(--p-bayhive-autocomplete-control-height);
  max-height: var(--p-bayhive-autocomplete-control-height);
}

html[data-ui-brand='bayhive'] .bayhive-autocomplete-shell .p-autocomplete.bayhive-autocomplete--selection-overflow:not(.bayhive-autocomplete--selection-expanded) .p-autocomplete-input-chip {
  block-size: 1px;
  clip-path: inset(50%);
  inline-size: 1px;
  overflow: hidden;
  position: absolute;
  white-space: nowrap;
}

html[data-ui-brand='bayhive'] .bayhive-autocomplete-shell .p-autocomplete.bayhive-autocomplete--selection-overflow.bayhive-autocomplete--has-selection-overflow .p-autocomplete-input-multiple {
  padding-inline-end: 5.5rem;
}

html[data-ui-brand='bayhive'] .bayhive-autocomplete-shell .bayhive-autocomplete-overflow-chip.p-chip {
  background: light-dark(${dt('surface.100')}, ${dt('surface.700')});
  border: 1px solid ${dt('content.border.color')};
  color: light-dark(${dt('surface.900')}, ${dt('surface.0')});
  cursor: help;
  font-size: var(--p-bayhive-autocomplete-chip-font-size);
  inset-block-end: var(--p-bayhive-autocomplete-input-padding-y);
  inset-inline-end: calc(var(--p-bayhive-autocomplete-control-height) + ${dt('bayhive.spacing.xs')});
  line-height: 1;
  min-height: var(--p-bayhive-autocomplete-chip-height);
  padding: 0.125rem var(--p-bayhive-autocomplete-chip-padding-x);
  position: absolute;
  z-index: 1;
}

html[data-ui-brand='bayhive'] .bayhive-autocomplete-shell .bayhive-autocomplete-overflow-chip.p-chip .p-chip-label {
  font-size: var(--p-bayhive-autocomplete-chip-font-size);
  line-height: 1;
}

html[data-ui-brand='bayhive'] .bayhive-autocomplete-shell:has(> .bayhive-autocomplete--selection-expanded) .bayhive-autocomplete-overflow-chip.p-chip {
  inset-block-end: auto;
  inset-block-start: var(--p-bayhive-autocomplete-input-padding-y);
}

html[data-ui-brand='bayhive'] .bayhive-autocomplete-shell .bayhive-autocomplete-overflow-chip.p-chip:is(:hover, :focus-visible) {
  background: light-dark(${dt('surface.200')}, ${dt('surface.600')});
  border-color: var(--p-primary-border-color, ${dt('primary.color')});
  outline: ${dt('form.field.focus.ring.width')} ${dt('form.field.focus.ring.style')} ${dt('form.field.focus.ring.color')};
  outline-offset: ${dt('form.field.focus.ring.offset')};
}

html[data-ui-brand='bayhive'] .bayhive-autocomplete-overflow-tooltip .p-tooltip-text {
  max-width: 24rem;
  white-space: pre-line;
}

/* Self-appended AutoComplete overlays use the field width. Dialog fields use
   the document portal; option-header controls remain keyboard-reachable through
   the wrapper in either placement. */
html[data-ui-brand='bayhive'] .p-autocomplete > .p-autocomplete-overlay.bayhive-field-overlay {
  inline-size: 100%;
  max-inline-size: 100%;
}

html[data-ui-brand='bayhive'] .bayhive-field-overlay .p-virtualscroller {
  overscroll-behavior-block: contain;
  scrollbar-gutter: stable;
}

html[data-ui-brand='bayhive'] .bayhive-field-overlay-option-row {
  align-items: center;
  display: grid;
  gap: var(--p-navigation-item-gap);
  grid-template-columns: var(--p-checkbox-width) minmax(0, 1fr);
  min-width: 0;
}

html[data-ui-brand='bayhive'] .bayhive-field-overlay-option-row--header {
  border-block-end: 1px solid var(--p-content-border-color);
  border-radius: var(--p-autocomplete-option-border-radius);
  padding: var(--p-autocomplete-option-padding);
}

html[data-ui-brand='bayhive'] .bayhive-field-overlay-option-row--header:is(
  :hover,
  :has(:focus-visible)
),
html[data-ui-brand='bayhive'] .bayhive-autocomplete-overlay--header-active .bayhive-field-overlay-option-row--header {
  background: var(--p-autocomplete-option-focus-background);
  color: var(--p-autocomplete-option-focus-color);
}

html[data-ui-brand='bayhive'] .bayhive-autocomplete-overlay--header-active .p-autocomplete-option:not(.p-autocomplete-option-selected) {
  background: transparent;
  color: var(--p-autocomplete-option-color);
}

html[data-ui-brand='bayhive'] .bayhive-autocomplete-overlay--header-active .p-autocomplete-option-selected {
  background: var(--p-autocomplete-option-selected-background);
  color: var(--p-autocomplete-option-selected-color);
}

html[data-ui-brand='bayhive'] .bayhive-field-overlay-option-checkbox--decorative {
  pointer-events: none;
}

html[data-ui-brand='bayhive'] .bayhive-field-overlay-option-content {
  display: grid;
  gap: 0.125rem;
  min-width: 0;
}

html[data-ui-brand='bayhive'] .bayhive-field-overlay-option-content :is(span, small) {
  overflow-wrap: anywhere;
}

html[data-ui-brand='bayhive'] .bayhive-field-overlay-option-content small {
  color: var(--p-text-muted-color);
}

/* Bootstrap's later anchor selector otherwise recolors and underlines PrimeVue
   menu entries, defeating Lara's menu state tokens. */
html[data-ui-brand='bayhive'] .p-menu-item-link:is(:hover, :focus) {
  color: inherit;
  text-decoration: none;
}

html[data-ui-brand='bayhive'] .shop-management-settings-menu .p-menu-list:focus-visible .p-menu-item.p-focus > .p-menu-item-content {
  box-shadow: ${dt('form.field.focus.ring.shadow')};
}

@media (prefers-reduced-motion: reduce) {
  html[data-ui-brand='bayhive'] *,
  html[data-ui-brand='bayhive'] *::before,
  html[data-ui-brand='bayhive'] *::after {
    animation-duration: 0.01ms;
    animation-iteration-count: 1;
    scroll-behavior: auto;
    transition-duration: 0.01ms;
  }
}
`;

export default utilityStyles;
